import {announceCart} from './ayume-cart-events.js';
import {withCartWriteLock} from './ayume-cart-coordinator.js';

export function notificationProperties(config, accountUrl, fallback, origin) {
  const leadTime = config?.leadTime?.trim();
  // The native setting is independent of authentication. The app's account
  // route can point at sign-in for guests, so a truthy app URL is not enough.
  for (const candidate of [fallback?.measurementsUrl, accountUrl]) {
    if (typeof candidate !== 'string' || !candidate.trim()) continue;
    try {
      const url = new URL(candidate, origin);
      url.search = ''; url.hash = '';
      if (leadTime && url.protocol === 'https:' && !url.username && !url.password && /^\/pages\/[^/]+\/?$/.test(url.pathname)) {
        return {_ayume_production_lead_time: leadTime, _ayume_measurements_url: url.href};
      }
    } catch { /* Try the other public page URL; never invent an account link. */ }
  }
  throw new Error('PURCHASE_CONTEXT_MISSING');
}

export function hasNotificationProperties(line, expected) {
  return Object.entries(expected).every(([key,value])=>line.properties?.[key]===value);
}

export function findVariant(variants, options) {
  return variants.find(v => v.options.length === options.length && v.options.every((value, i) => value === options[i]));
}

export function savedProfile(data, variant, loggedIn) {
  if (!loggedIn || !variant) return null;
  const requirement = data?.variantRequirements?.[String(variant.id)];
  if (variant.family === 'veryShortCustom' && requirement?.kind === 'veryShortCustom') return data.measurements?.veryShortCustom ?? null;
  if (requirement?.kind === 'classic' && requirement.profileKey === variant.family) return data.measurements?.classic?.[variant.family] ?? null;
  return null;
}

export function verifiedMeasuredLine(cart, variant, ref, notification = {}) {
  return cart?.items?.some(item => String(item.variant_id) === String(variant.id) && item.quantity === 1 && item.properties?._ayume_measurement_ref === ref && item.properties?._ayume_family === variant.family && item.properties?._ayume_measurement_status === 'recorded' && hasNotificationProperties(item, notification));
}

// Navigation must not turn an uncertain POST into permission for a second add.
// Receipts contain identifiers and quantities only, never measurement values.
export function purchaseReturnState(cart, receipt) {
  if (!Array.isArray(cart?.items)) return 'uncertain';
  if (!receipt.attempted) return 'ready';
  const items = cart.items.filter(item => String(item.variant_id) === String(receipt.variantId) && item.quantity > 0);
  const present = receipt.isKit
    ? items.reduce((sum,item) => sum + item.quantity,0) > receipt.beforeQuantity
    : items.some(item => receipt.reference
      ? item.properties?._ayume_measurement_ref === receipt.reference
      : item.properties?._ayume_measurement_status === 'recorded' && item.properties?._ayume_measurement_ref && !receipt.beforeRefs?.includes(item.properties._ayume_measurement_ref));
  if (present) return 'present';
  // A confirmed line may subsequently have been removed deliberately in cart.
  return receipt.confirmed || receipt.observed ? 'ready' : 'uncertain';
}

async function readCart(root) {
  const response = await fetch(`${root}cart.js`, {cache:'no-store', headers:{Accept:'application/json'}});
  if (!response.ok) throw new Error('CART_READ');
  return response.json();
}

class AyumeProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
    this.strings = this.config.strings;
    this.variantInput = this.querySelector('[name="id"]');
    this.options = this.config.variants.find(v => String(v.id) === this.variantInput.value)?.options.slice() ?? [];
    this.mode = 'saved';
    this.addedConfigurations = new Set();
    this.isKit = this.dataset.kit === 'true';
    this.status = this.querySelector('[data-status]');
    this.addButton = this.querySelector('[data-add]');
    this.cartLink = this.querySelector('[data-cart-link]');
    this.dialog = this.querySelector('.ayume-config-dialog');
    this.content = this.querySelector('.ayume-config-content');
    this.home = this.querySelector('.ayume-config-home');
    this.media = matchMedia('(max-width: 989px)');
    this.configTrigger = this.querySelector('[data-open-config]');
    this.priceLabels = [...this.querySelectorAll('[data-price]')];
    this.productBar = this.querySelector('.ayume-product-bar');
    this.productBarHome = this.productBar?.parentElement;
    this.syncProductBar = () => {
      if(!this.productBar)return;
      const parent = this.media.matches ? this.productBarHome : document.querySelector('.av-header-shell');
      if(parent && this.productBar.parentElement !== parent)parent.append(this.productBar);
    };
    this.syncProductBar();
    this.media.addEventListener('change',this.syncProductBar);
    this.configTrigger.addEventListener('click',()=>this.openConfig());
    this.draftKey = `ayume:configuration:${this.dataset.product}`;
    this.receiptKey = `ayume:purchase-receipts:${this.dataset.product}`;
    try {this.receipts = JSON.parse(sessionStorage.getItem(this.receiptKey) || '{}');} catch {this.receipts = {};}
    try {
      const draft = JSON.parse(sessionStorage.getItem(this.draftKey) || 'null');
      const requested = new URL(location.href).searchParams.get('variant');
      if (!requested && draft?.variantId) {
        const restored = this.config.variants.find(v => String(v.id) === String(draft.variantId) && v.available);
        if (restored) this.options = restored.options.slice();
      }
      if (draft?.mode === 'pending') this.mode = 'pending';
    } catch {}
    this.querySelectorAll('[data-option]').forEach(input => input.checked = this.options[Number(input.dataset.option)] === input.value);
    this.querySelectorAll('[name^="measurement-"]').forEach(input => input.checked = input.value === this.mode);
    this.addEventListener('change', event => {
      if (event.target.matches('[data-option]')) {this.options[Number(event.target.dataset.option)] = event.target.value; this.render();}
      if (event.target.matches('[name^="measurement-"]')) {this.mode = event.target.value; this.render();}
    });
    this.addEventListener('click', event => {
      const target = event.target.closest('button,a');
      if (!target) return;
      if (target.matches('[data-close]')) target.closest('dialog').close();
      if (target.matches('[data-media]') && !target.closest('ayume-media-gallery')) {
        this.querySelectorAll('.ayume-gallery-item').forEach(item => {item.hidden = item.id !== target.dataset.media; item.querySelector('video')?.pause();});
        this.querySelectorAll('[data-media]').forEach(item => item.setAttribute('aria-pressed', String(item === target)));
      }
      if (target.matches('[data-zoom]')) {
        const image = target.querySelector('img').cloneNode(true);
        this.querySelector('[data-zoom-content]').replaceChildren(image);
        this.querySelector('.ayume-zoom-dialog').showModal();
      }
      if (target.matches('[data-measures-link]')) {this.returnPending = true; this.saveDraft();}
    });
    this.dialog.addEventListener('close', () => {this.home.append(this.content); this.configTrigger.focus();});
    this.media.addEventListener('change', () => {if (this.dialog.open && !this.media.matches) this.dialog.close();});
    this.addButton.addEventListener('click', () => this.purchase());
    this.onPageShow = event => {if(event.persisted)this.reconcileReturn();};
    window.addEventListener('pageshow',this.onPageShow);
    this.addEventListener('ayume:measurement-readiness', () => this.renderProfile());
    window.addEventListener('focus', () => {if (this.returnPending) {this.returnPending = false; this.refreshProfile();}});
    document.addEventListener('visibilitychange', () => {if (!document.hidden && this.returnPending) {this.returnPending = false; this.refreshProfile();}});
    this.render();
    this.reconcileReturn();
    if (!this.isKit) this.refreshProfile();
  }
  saveDraft() {
    try {sessionStorage.setItem(this.draftKey, JSON.stringify({variantId:this.variant?.id,mode:this.mode}));} catch {}
  }
  disconnectedCallback() {
    window.removeEventListener('pageshow',this.onPageShow);
    this.media?.removeEventListener('change',this.syncProductBar);
    if(this.productBar && this.productBarHome)this.productBarHome.append(this.productBar);
  }
  openConfig() {
    if(!this.media.matches){this.content.scrollIntoView({block:'start',behavior:'auto'});this.content.focus({preventScroll:true});return;}
    this.querySelector('.ayume-config-slot').append(this.content);
    this.dialog.showModal();
  }
  render() {
    this.variant = findVariant(this.config.variants, this.options);
    const receipt = this.receipts?.[`${this.variant?.id}:${this.mode}`];
    this.uncertain = receipt?.state === 'uncertain' || receipt?.state === 'writing';
    this.added = this.addedConfigurations.has(`${this.variant?.id}:${this.mode}`);
    if (this.variant) {
      this.variantInput.value = String(this.variant.id);
      this.variantInput.dispatchEvent(new Event('change', {bubbles:true}));
      this.priceLabels.forEach(el=>el.textContent=this.variant.price);
      this.querySelector('[data-summary]').textContent = this.variant.title === 'Default Title' ? '' : this.variant.title;
      const url = new URL(location.href); url.searchParams.set('variant', String(this.variant.id)); history.replaceState(null,'',url);
    }
    this.querySelectorAll('[data-option]').forEach(input => {
      const selection = [...this.options]; selection[Number(input.dataset.option)] = input.value;
      const possible = findVariant(this.config.variants, selection);
      input.disabled = this.busy || !possible?.available;
    });
    this.querySelectorAll('[name^="measurement-"]').forEach(input => input.disabled = !!this.busy);
    this.querySelector('[data-pending-copy]')?.toggleAttribute('hidden', this.mode !== 'pending');
    this.querySelector('[data-measure-state]')?.toggleAttribute('hidden', this.mode === 'pending');
    this.saveDraft();
    this.renderProfile();
  }
  bridgeData(scope = this) {
    const block = scope.querySelector('#ayume-mx');
    const json = scope.querySelector('#ayume-mx-data');
    return {block, data:json ? JSON.parse(json.textContent) : null, loggedIn:block?.dataset.loggedIn === 'true'};
  }
  renderProfile() {
    const bridge = this.freshBridge ?? this.bridgeData();
    const profile = savedProfile(bridge.data, this.variant, bridge.loggedIn);
    const state = this.querySelector('[data-profile-status]');
    if (state) state.textContent = !bridge.block ? this.strings.connecting : !bridge.loggedIn ? this.strings.login_required : profile ? this.strings.profile_ready : this.strings.profile_missing;
    const link = this.querySelector('[data-measures-link]');
    if (link && bridge.block) {
      let href = bridge.loggedIn ? bridge.block.dataset.accountUrl : bridge.block.dataset.loginUrl;
      if (bridge.loggedIn && this.variant?.family && this.variant.family !== 'veryShortCustom') {
        const url = new URL(href,location.origin);
        if (url.pathname.includes('/pages/')) url.pathname = `${url.pathname.replace(/\/$/,'')}/classic/${this.variant.family}`;
        href = url.href;
      }
      if (!bridge.loggedIn) {
        const login = new URL(href, location.origin);
        const returnTo = new URL(location.href);
        returnTo.searchParams.delete('section_id');
        login.searchParams.set('return_to', returnTo.pathname + returnTo.search);
        href = login.href;
      }
      link.href = href;
      link.textContent = `${bridge.loggedIn ? this.strings.open_measures : this.strings.login} ↗`;
    }
    const eligible = this.variant?.available && (this.isKit || (this.mode === 'pending' ? this.variant.kitId && this.variant.family : profile && bridge.block?.dataset.storefrontToken));
    this.addButton.disabled = !!(this.busy || this.uncertain || this.added || !eligible);
    this.addButton.textContent = this.busy ? this.strings.adding : this.added ? this.strings.added : !this.variant?.available ? this.strings.unavailable : `${this.isKit ? this.strings.add_kit : this.mode === 'pending' ? this.strings.add_set_kit : this.strings.add_set} ↗`;
  }
  async freshSection() {
    const url = new URL(this.dataset.productUrl, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const view = new URL(location.href).searchParams.get('view');
    if (view) url.searchParams.set('view', view);
    url.searchParams.set('section_id', this.dataset.section);
    url.searchParams.set('variant', String(this.variant.id));
    const response = await fetch(url,{cache:'no-store'});
    if (!response.ok) throw new Error('PROFILE_READ');
    const dom = new DOMParser().parseFromString(await response.text(),'text/html');
    const config = dom.querySelector('[data-product-config]');
    if (!config) throw new Error('PRODUCT_READ');
    return {dom,config:JSON.parse(config.textContent),bridge:this.bridgeData(dom)};
  }
  async refreshProfile() {
    try {const fresh = await this.freshSection(); this.freshBridge = fresh.bridge; this.renderProfile();}
    catch {this.setStatus(this.strings.refresh_error, true);}
  }
  setStatus(message,error=false) {this.status.textContent=message;this.status.dataset.error=String(error);}
  recordReceipt(receipt) {
    this.receipts[`${receipt.variantId}:${receipt.mode}`] = receipt;
    try {sessionStorage.setItem(this.receiptKey,JSON.stringify(this.receipts));} catch {}
  }
  async reconcileReturn() {
    if(this.busy || this.reconciling || !Object.keys(this.receipts).length)return;
    this.reconciling=true;this.busy=true;this.renderProfile();
    try {
      const cart=await readCart(this.dataset.root);
      for(const [key,receipt] of Object.entries(this.receipts)){
        const state=purchaseReturnState(cart,receipt);
        if(state==='ready'){delete this.receipts[key];this.addedConfigurations.delete(key);}
        else {receipt.state=state==='present'?'present':'uncertain';if(state==='present'){receipt.observed=true;this.addedConfigurations.add(key);}}
      }
      try {sessionStorage.setItem(this.receiptKey,JSON.stringify(this.receipts));} catch {}
      const current=this.receipts[`${this.variant?.id}:${this.mode}`];
      this.cartLink.hidden=!current;
      if(current)this.cartLink.textContent=this.strings.verify_cart;
      this.setStatus(current ? current.confirmed ? this.strings.added_success : this.strings.check_cart : '',Boolean(current&&!current.confirmed));
    } catch {
      Object.values(this.receipts).forEach(receipt=>receipt.state='uncertain');
      this.cartLink.hidden=false;this.cartLink.textContent=this.strings.verify_cart;
      this.setStatus(this.strings.check_cart,true);
    } finally {this.busy=false;this.reconciling=false;this.render();}
  }
  async purchase() {
    if (this.busy || this.added || this.uncertain || !this.variant?.available) return;
    this.busy=true;this.render();this.setStatus('');
    let attempted=false;
    const receipt={variantId:this.variant.id,mode:this.mode,isKit:this.isKit,attempted:false,state:'writing'};
    try {
      const fresh=await this.freshSection();
      const live=fresh.config.variants.find(v=>String(v.id)===String(this.variant.id));
      if (!live?.available || live.family!==this.variant.family || String(live.kitId)!==String(this.variant.kitId)) throw new Error('CHANGED');
      this.variant=live;this.priceLabels.forEach(el=>el.textContent=live.price);
      this.freshBridge=fresh.bridge;
      const accountUrl = fresh.bridge.block?.dataset.accountUrl;
      const purchaseContext = JSON.parse(document.querySelector('[data-ayume-purchase-context]')?.textContent || '{}');
      const notification = notificationProperties(fresh.config, accountUrl, purchaseContext, location.origin);
      const measurementsUrl = notification._ayume_measurements_url;
      if (this.isKit) {
        if (!navigator.locks?.request) throw new Error('CART_LOCK_UNAVAILABLE');
        const locked = await withCartWriteLock(async()=>{
          const before=await readCart(this.dataset.root);
          const beforeQuantity=before.items.filter(i=>String(i.variant_id)===String(live.id)).reduce((n,i)=>n+i.quantity,0);
          receipt.beforeQuantity=beforeQuantity;receipt.attempted=true;this.recordReceipt(receipt);
          attempted=true;
          const response=await fetch(`${this.dataset.root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:live.id,quantity:1,properties:{_ayume_kit_family:live.family,...notification}}]})});
          if (!response.ok) throw new Error('ADD_FAILED');
          const after=await readCart(this.dataset.root);
          if (after.items.filter(i=>String(i.variant_id)===String(live.id)).reduce((n,i)=>n+i.quantity,0)!==beforeQuantity+1) throw new Error('VERIFY');
          if (!after.items.some(i=>String(i.variant_id)===String(live.id) && hasNotificationProperties(i, notification))) throw new Error('VERIFY');
        }, {recheck:()=>readCart(this.dataset.root)});
        if (locked?.ok === false) throw new Error('CART_BUSY');
      } else if (this.mode==='pending') {
        if (!purchaseContext.pendingModuleUrl) throw new Error('PURCHASE_CONTEXT_MISSING');
        const {addPendingPurchase}=await import(purchaseContext.pendingModuleUrl);
        const result=await addPendingPurchase({setVariantId:live.id,kitVariantId:live.kitId,family:live.family,leadTime:notification._ayume_production_lead_time,measurementsUrl},{root:this.dataset.root,onAttempt:reference=>{attempted=true;receipt.attempted=true;receipt.reference=reference;this.recordReceipt(receipt);}});
        attempted=result.attempted;
        if (!result.ok) throw new Error(result.status==='cart_busy' ? 'CART_BUSY' : 'CHECK_CART');
      } else {
        const profile=savedProfile(fresh.bridge.data,live,fresh.bridge.loggedIn);
        if (!profile) throw new Error('PROFILE_MISSING');
        const script=this.querySelector('script[src*="/vertical-slice.js"]');
        if (!script || !fresh.bridge.block?.dataset.storefrontToken) throw new Error('BRIDGE_MISSING');
        const base=new URL('.',script.src);
        const [storefront,snapshot,transport]=await Promise.all([
          import(new URL('lib-storefront.generated.js',base).href),
          import(new URL('lib-snapshot.generated.js',base).href),
          import(new URL('lib-cart-compaction.generated.js',base).href)
        ]);
        const assignment=live.family==='veryShortCustom' ? snapshot.buildVeryShortCustomAssignment(profile) : snapshot.buildClassicAssignment(live.family,profile);
        const fetchGraphQL=transport.makeFetchGraphQL({shopDomain:fresh.bridge.block.dataset.shopDomain,storefrontToken:fresh.bridge.block.dataset.storefrontToken});
        const before=await readCart(this.dataset.root);
        receipt.beforeRefs=before.items.map(item=>item.properties?._ayume_measurement_ref).filter(Boolean);
        receipt.attempted=true;this.recordReceipt(receipt);
        attempted=true;
        const lineAttributes=[{key:'_ayume_family',value:live.family},{key:'_ayume_measurement_status',value:'recorded'}];
        lineAttributes.push(...Object.entries(notification).map(([key,value])=>({key,value})));
        const result=await storefront.createAssignment(fetchGraphQL,{useNativeCart:true,cartId:`gid://shopify/Cart/${before.token}`,variantId:`gid://shopify/ProductVariant/${live.id}`,quantity:1,assignment,lineAttributes});
        if (!result.ok) throw new Error('ASSIGNMENT_FAILED');
        receipt.reference=result.ref;this.recordReceipt(receipt);
        const after=await readCart(this.dataset.root);
        const stored=await storefront.loadSnapshot(fetchGraphQL,`gid://shopify/Cart/${after.token}`);
        if (!verifiedMeasuredLine(after,live,result.ref,notification) || !stored.ok || JSON.stringify(stored.snapshot.assignments[result.ref])!==JSON.stringify(assignment)) throw new Error('VERIFY');
      }
      this.addedConfigurations.add(`${live.id}:${this.mode}`);
      receipt.state='present';receipt.confirmed=true;this.recordReceipt(receipt);
      this.added=true;this.cartLink.hidden=false;this.setStatus(this.strings.added_success);
      const currentCart=await readCart(this.dataset.root);
      await announceCart(currentCart,{action:'add',context:'product',lines:[{merchandiseId:String(live.id),quantity:1}]});
    } catch(error) {
      const checkCart=attempted || error.message==='CART_BUSY';
      if(checkCart){receipt.state='uncertain';receipt.attempted=attempted;this.recordReceipt(receipt);}
      this.uncertain=checkCart;
      this.cartLink.hidden=!checkCart;
      if(checkCart)this.cartLink.textContent=this.strings.verify_cart;
      this.setStatus(checkCart ? this.strings.check_cart : error.message==='PROFILE_MISSING' ? this.strings.profile_missing : error.message==='CART_LOCK_UNAVAILABLE' ? this.strings.lock_unavailable : this.strings.retry, true);
    } finally {this.busy=false;this.render();}
  }
}
if (typeof customElements!=='undefined' && !customElements.get('ayume-product')) customElements.define('ayume-product',AyumeProduct);
